/**
 * hauer-garage.js
 * Estado da "garagem" (moto do cliente) compartilhado pelas seções hauer-*.
 *
 * Fonte dos dados: menu de navegação (linklist) marca > modelo > ano, o mesmo
 * que alimenta a página Shop By Bike. O layout imprime esse menu uma vez por página num
 * <script type="application/json" data-hauer-garage-data> (snippets/hauer-garage-data, A-24);
 * a coleção imprime a própria cópia com contagens. Cada init procura primeiro na seção, depois
 * no document. A-18: modelLabel() unifica a grafia das BMW ("S 1000 RR" -> "S1000 RR").
 *
 * Persistência: localStorage em `hauer:garage` e, por compatibilidade com o
 * banner já existente nas coleções (snippets/bike-selection-banner.liquid),
 * também em `hauer:selected-bike`.
 *
 * Eventos: dispara `hauer:garage:change` no document sempre que a moto muda;
 * `hauer:garage:open` (cancelável) quando o cliente pede para definir/trocar a moto.
 * 24/09/2026 (tickets A-02, A-03, A-04):
 *  - fits()/fitState(): token "open" em data-fit-tags = faixa aberta (qualquer ano >= mínimo encaixa);
 *    "open:all" = todos os anos; card sem tag de ano com garagem por ano vira "Check years".
 *  - bikeUrl(g, query): /collections/{handle}/{ano} (caminho legado de tag da Shopify) + query.
 *  - shipDate(days): hoje + N dias úteis, formato "Oct 8"; preenche os <span data-hauer-shipdate>.
 * 24/09/2026 (tickets A-05, A-09, A-11, A-13, A-19, A-20, A-22):
 *  - A-22: initIn(root) inicializa só o que está dentro de root (document no boot, e.target no
 *    shopify:section:load); cada init tem guarda data-hauer-ready; um único listener de
 *    hauer:garage:change percorre a lista `painters`.
 *  - A-09: openGarage() dispara `hauer:garage:open` (cancelável): a página de produto e a coleção
 *    respondem no lugar; sem resposta, rola até o card da home ou navega para /#hauer-garage.
 *  - A-05: fragmento pedido por section_id=hauer-garage-products (nome do arquivo); na página de
 *    produto descarta a própria peça e o mesmo tipo, prefere um card por tipo; erro/vazio volta ao
 *    texto padrão (+ sub_error); esqueleto .is-loading enquanto busca.
 *  - A-11: initCartUpsell() preenche [data-hauer-cart-upsell] no drawer com 2 peças da moto salva
 *    (fora do carrinho, tipos diferentes, 1 variante de preferência) e o "encaixa / não encaixa"
 *    por linha do carrinho ([data-hauer-cart-line]).
 *  - A-13: write()/clear() gravam a moto nos atributos do carrinho (/cart/update.js), publicam
 *    hauer_garage_set (Shopify.analytics + dataLayer); track() vive aqui para todos os bundles;
 *    a newsletter recebe a tag bike:{marca modelo ano}.
 *  - A-19: link "Shop parts for my bike" do carrinho vazio e botão de vídeos do Learn apontam para
 *    a moto salva.
 * 12/10/2026 (plano loja rica):
 *  - comentário 15a: initBar alterna a classe has-bike no chip do header e na faixa do celular
 *    (o estilo verde fica em hauer-base.css).
 *  - comentário 7: initBundleFit esconde o bundle do mês ([data-bundle-fit-tags]) quando a moto
 *    salva não casa com a moto do bundle (fitState); sem moto salva, mostra.
 * 12/10/2026 (versão Claude, etapa A: hero e caminho até as peças da moto):
 *  - Ano opcional no hero (data-year-optional): "All years" entra no topo do select Year e já vem
 *    marcado; o botão libera depois de Make + Model. "All years" de um modelo com faixa no menu
 *    ("S 1000 RR (2019-on)") grava g.range / g.y1 / g.y2: label() mostra "(2019-on)" e fitState()
 *    só aceita peças com ano dentro da faixa (peça sem tag de ano vira 'check'). Nada de outra geração.
 *  - Chips de 1 toque ([data-hauer-quick-bike], data-urls = coleções do menu): uma geração salva na
 *    hora; mais de uma abre "Which MT-09?" com as faixas do menu.
 *  - commit(): com data-inline="true" no botão e uma seção [data-inline-target] na página, salva,
 *    rola até a grade e espera a troca (sec.hauerProducts() devolve a promessa do último paint);
 *    só navega para a coleção se a grade falhar (ou demorar mais de 10 s).
 *  - modelKeys(): modelo do menu com apelidos ("GSX-S 1000 | 950", "ZX-6R | RR", "Z400 | Ninja 400")
 *    casa com as tags por qualquer apelido (GSX-S 1000, GSX-S 950, ZX-6R, ZX-6RR, Ninja 400...). Antes só o
 *    rótulo inteiro ("gsxs1000950") valia e a grade da GSX-S 1000 ficava com 7 de 26 peças. label() mostra
 *    " / " no lugar do " | ".
 *  - initProducts (data-inline-target): título "{n} parts fit your {bike}" com a contagem do
 *    fragmento (data-count = products_count, menos as peças que fitState descartou), eyebrow e link
 *    "See all parts for my bike" trocam com a moto; sem moto o link vira "Set my bike" (openGarage).
 * 12/10/2026 (versão Claude, etapa B: resto da home):
 *  - initCats ([data-hauer-cats]): com moto salva, os tiles de categoria escondem na hora a contagem do
 *    catálogo inteiro; quando o fragmento da moto chega (fetchGrid, o mesmo da grade de peças e já em
 *    cache), cada tile com data-types mostra "{n} for your {bike} · from $X" só com as peças que servem
 *    (fitState). Zero peças, sem data-types ou erro: nenhuma contagem. Sem moto: a contagem global volta.
 *    Coleção maior que o fragmento (data-count > cards): "{n}+" e sem o "from $X".
 *  - initBundleFit: data-bundle-need-bike esconde o bundle também sem moto salva; a seção só aparece
 *    (classe is-shown) quando a moto salva casa com fit_tags. No editor de temas aparece sempre.
 *    bundleCoversRange: "All years" de uma geração só vê o bundle se os anos dele cobrem a geração inteira.
 * Aprovado em 23/09/2026 a partir do protótipo. Remover: apagar este arquivo
 * e as seções hauer-garage-bar, hauer-hero-garage e hauer-garage-products.
 */
(function () {
  'use strict';
  // Carregado no layout e também por seções: só inicializa uma vez.
  if (window.HauerGarage) return;

  var KEY = 'hauer:garage';
  var LEGACY_KEY = 'hauer:selected-bike';
  var SECTION_FILE = 'hauer-garage-products';

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var g = raw ? JSON.parse(raw) : null;
      if (g && g.model) g.model = normModel(g.model); // A-18: garagens salvas antes da grafia única
      return g && g.brand && g.model ? g : null;
    } catch (e) { return null; }
  }

  /* ---------- Analytics (A-13): um track() para todos os bundles ---------- */
  function track(event, data) {
    var payload = Object.assign({ event: 'hauer_' + event }, data || {});
    try { document.dispatchEvent(new CustomEvent('hauer:track', { detail: payload })); } catch (e) { /* noop */ }
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.dataLayer.push === 'function') window.dataLayer.push(payload);
    } catch (e) { /* noop */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(payload.event, data || {});
      }
    } catch (e) { /* noop */ }
  }

  /* Atributos do carrinho (A-13): a moto chega ao pedido mesmo em adições fora da página de produto. */
  function syncCart(g) {
    if (typeof window.fetch !== 'function') return;
    var root = (window.theme && window.theme.routes && window.theme.routes.root) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    root = String(root).replace(/\/$/, '');
    try {
      fetch(root + '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { bike: label(g) || '', bike_handle: g && g.handle ? g.handle : '' } })
      }).catch(function () { /* offline ou bloqueado: a moto segue no localStorage */ });
    } catch (e) { /* noop */ }
  }

  function write(g, source) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(g));
      window.localStorage.setItem(LEGACY_KEY, JSON.stringify({
        brand: g.brand, model: g.model, year: g.year, path: g.url ? g.url.split('?')[0] : ''
      }));
    } catch (e) { /* storage bloqueado: segue sem persistir */ }
    document.dispatchEvent(new CustomEvent('hauer:garage:change', { detail: g }));
    track('garage_set', { brand: g.brand, model: g.model, year: g.year, handle: g.handle || '', source: source || g.source || 'unknown' });
    syncCart(g);
  }

  function clear(source) {
    try { window.localStorage.removeItem(KEY); window.localStorage.removeItem(LEGACY_KEY); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('hauer:garage:change', { detail: null }));
    track('garage_clear', { source: source || 'unknown' });
    syncCart(null);
  }

  function label(g) {
    if (!g) return '';
    // 12/10/2026 (etapa A): "All years" de um modelo com faixa mostra a geração: "BMW S1000 RR (2019-on)".
    var yr = g.year && g.year !== 'all' ? g.year : (g.range ? '(' + g.range + ')' : '');
    return [g.brand, String(g.model || '').replace(/\s*\|\s*/g, ' / '), yr].filter(Boolean).join(' ');
  }

  /**
   * 12/10/2026 (etapa A): faixa de anos no rótulo do modelo do menu.
   * "(2019-on)" -> 2019..9999, "(2010-2014)", "(2018 & Newer)", "(2016 & Older)". Sem faixa: null.
   */
  function rangeOf(title) {
    var t = String(title || '');
    var m = /\((\d{4})\s*[-–]\s*(\d{4}|on|up|newer|present)\)/i.exec(t);
    if (m) {
      var open = !/^\d{4}$/.test(m[2]);
      return { y1: +m[1], y2: open ? 9999 : +m[2], text: m[1] + (open ? '-on' : '-' + m[2]) };
    }
    m = /\((\d{4})\s*(?:&amp;|&|and)\s*(newer|up|older)\)/i.exec(t);
    if (m) {
      var older = /older/i.test(m[2]);
      return { y1: older ? 0 : +m[1], y2: older ? +m[1] : 9999, text: m[1] + (older ? ' & older' : ' & newer') };
    }
    return null;
  }

  /**
   * Anos de um modelo. Se o menu tiver filhos (ano por link), usa-os.
   * Senão, deriva do título "(2019-on)" / "(2010-2014)". Sem nada: "All years".
   */
  function yearsFor(model) {
    if (model.years && model.years.length) return model.years;
    var m = /\((\d{4})\s*[-–]\s*(\d{4}|on|up|newer|present)\)/i.exec(model.title || '');
    if (!m) return [{ title: 'All years', url: model.url, value: 'all' }];
    var y1 = parseInt(m[1], 10);
    var y2 = /^\d{4}$/.test(m[2]) ? parseInt(m[2], 10) : new Date().getFullYear() + 1;
    var out = [];
    for (var y = y2; y >= y1; y--) out.push({ title: String(y), url: model.url, value: String(y) });
    return out;
  }

  /* A-18: grafia única das BMW. Menu e coleções dizem "S 1000 RR"; títulos e cards, "S1000 RR". */
  var BMW_SPACING = { 's 1000 rr': 'S1000 RR', 's 1000 r': 'S1000 R', 's 1000 xr': 'S1000 XR', 'f 900 r': 'F900 R', 'f 800 r': 'F800 R', 'g 310 r': 'G310 R' };
  function normModel(name) { var t = String(name || '').trim(); return BMW_SPACING[t.toLowerCase().replace(/\s+/g, ' ')] || t; }
  function modelLabel(model) {
    return normModel((model.title || '').replace(/\s*\(.*\)\s*$/, ''));
  }

  /**
   * URL da coleção da moto. Com ano de 4 dígitos usa o caminho legado de tag da Shopify
   * (/collections/{handle}/{ano}): os produtos já levam uma tag por ano, então a lista, a
   * contagem e a paginação vêm filtradas do servidor sem depender de app ou admin.
   */
  function bikeUrl(g, query) {
    var handle = g && g.handle ? String(g.handle).replace(/^\/+|\/+$/g, '') : 'all';
    var url = '/collections/' + handle;
    if (g && handle !== 'all' && /^\d{4}$/.test(String(g.year || ''))) url += '/' + g.year;
    var q = String(query || '').replace(/^[?&]+/, '');
    return q ? url + '?' + q : url;
  }

  /** Hoje + N dias úteis (seg-sex), no formato "Oct 8". */
  function shipDate(days) {
    var n = parseInt(days, 10); if (!(n > 0)) n = 10;
    var d = new Date(); d.setHours(12, 0, 0, 0);
    while (n > 0) { d.setDate(d.getDate() + 1); var wd = d.getDay(); if (wd !== 0 && wd !== 6) n--; }
    try { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch (e) { return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()] + ' ' + d.getDate(); }
  }
  function paintShipDates(root) {
    (root || document).querySelectorAll('[data-hauer-shipdate]').forEach(function (el) {
      if (el.dataset.hauerShipdateDone) return;
      el.textContent = shipDate(el.dataset.hauerShipdate);
      el.dataset.hauerShipdateDone = '1';
    });
  }

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function fill(tpl, map) { return String(tpl || '').replace(/\{(\w+)\}/g, function (_, k) { return map[k] != null ? map[k] : ''; }); }

  /* ---------- Lista única de pintores (A-22): um listener de hauer:garage:change ---------- */
  var painters = [];
  function onChange(fn) { painters.push(fn); }
  document.addEventListener('hauer:garage:change', function () {
    painters.forEach(function (fn) { try { fn(); } catch (e) { /* uma seção com erro não derruba as outras */ } });
  });
  function once(el) { if (!el || el.dataset.hauerReady) return false; el.dataset.hauerReady = '1'; return true; }

  /* ---------- "Set my bike" / "Change" em qualquer página (A-09) ---------- */
  function openGarage(url) {
    var ev = new CustomEvent('hauer:garage:open', { cancelable: true, detail: { garage: read() } });
    document.dispatchEvent(ev);
    if (ev.defaultPrevented) return true;
    var target = document.querySelector('[data-hauer-garage-card]');
    if (target && target.offsetParent !== null) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var s = target.querySelector('select'); if (s) setTimeout(function () { s.focus(); }, 400);
      return true;
    }
    window.location.href = url || '/#hauer-garage';
    return false;
  }

  window.HauerGarage = { read: read, write: write, clear: clear, label: label, yearsFor: yearsFor, rangeOf: rangeOf, modelLabel: modelLabel, bikeUrl: bikeUrl, shipDate: shipDate, paintShipDates: paintShipDates, track: track, openGarage: openGarage, onChange: onChange };

  /* ---------- Seletor (hero) ---------- */
  function initSelector(root) {
    if (!once(root)) return;
    var dataEl = root.querySelector('[data-hauer-garage-data]') || document.querySelector('[data-hauer-garage-data]');
    if (!dataEl) return;
    var brands;
    try { brands = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var selMake = root.querySelector('[data-hauer-make]');
    var selModel = root.querySelector('[data-hauer-model]');
    var selYear = root.querySelector('[data-hauer-year]');
    var go = root.querySelector('[data-hauer-go]');
    var card = root.querySelector('[data-hauer-garage-card]');
    var savedEl = root.querySelector('[data-hauer-saved-label]');
    var changeBtn = root.querySelector('[data-hauer-change]');
    if (!selMake || !selModel || !selYear || !go) return;

    function opt(sel, items, placeholder) {
      sel.innerHTML = '';
      var ph = document.createElement('option');
      ph.value = ''; ph.textContent = placeholder; ph.disabled = true; ph.selected = true;
      sel.appendChild(ph);
      items.forEach(function (it, i) {
        var o = document.createElement('option');
        o.value = String(i); o.textContent = it.title;
        sel.appendChild(o);
      });
      sel.disabled = items.length === 0;
    }

    var current = { brand: null, model: null, years: [] };
    var silent = false;
    // 12/10/2026 (etapa A): ano opcional só onde a seção pede (hero); a página de produto continua pedindo o ano.
    var yearOpt = root.hasAttribute('data-year-optional');
    var allLabel = selYear.dataset.allLabel || 'All years';
    function yearList(m) {
      var ys = yearsFor(m);
      if (!yearOpt) return ys;
      if (ys.length === 1 && ys[0].value === 'all') return [{ title: allLabel, url: ys[0].url, value: 'all' }];
      return [{ title: allLabel, url: m.url, value: 'all' }].concat(ys);
    }

    opt(selMake, brands, selMake.dataset.placeholder || 'Make');
    opt(selModel, [], selModel.dataset.placeholder || 'Model');
    opt(selYear, [], selYear.dataset.placeholder || 'Year');
    go.disabled = true;

    // Teclado (24/09/2026): no Windows, setas num select fechado disparam change a cada tecla;
    // só avançamos o foco sozinho para mouse/toque (WCAG 3.2.2). Remover: apagar este bloco e o '!keyed' abaixo.
    var keyed = false;
    [selMake, selModel].forEach(function (s) {
      s.addEventListener('keydown', function (e) { if (e.key !== 'Tab' && e.key !== 'Enter') keyed = true; });
      s.addEventListener('pointerdown', function () { keyed = false; });
      s.addEventListener('blur', function () { keyed = false; });
    });

    selMake.addEventListener('change', function () {
      current.brand = brands[+selMake.value];
      var models = (current.brand.models || []).map(function (m) { return { title: modelLabel(m) + (m.title.match(/\(.*\)/) ? ' ' + m.title.match(/\(.*\)/)[0] : ''), raw: m }; });
      opt(selModel, models, selModel.dataset.placeholder || 'Model');
      opt(selYear, [], selYear.dataset.placeholder || 'Year');
      current.model = null; go.disabled = true;
      if (!silent && !keyed) selModel.focus();
    });

    selModel.addEventListener('change', function () {
      var m = current.brand.models[+selModel.value];
      current.model = m;
      current.years = yearList(m);
      opt(selYear, current.years, selYear.dataset.placeholder || 'Year');
      if (yearOpt) { selYear.value = '0'; go.disabled = false; return; } // "All years" já marcado
      go.disabled = true;
      if (!silent && !keyed) selYear.focus();
    });

    selYear.addEventListener('change', function () { go.disabled = selYear.value === ''; });

    /** Moto a gravar a partir de marca + modelo do menu + ano ({ value, url } ou null = todos os anos). */
    function garageFor(brand, model, y) {
      var url = (y && y.url) || model.url || '';
      var g = {
        brand: brand.title,
        model: modelLabel(model),
        year: y ? String(y.value || y.title) : 'all',
        handle: (url.split('?')[0].match(/\/collections\/([^/?#]+)/) || [])[1] || ''
      };
      if (g.year === 'all') { var r = rangeOf(model.title); if (r) { g.range = r.text; g.y1 = r.y1; g.y2 = r.y2; } }
      var q = 'sbb_brand=' + encodeURIComponent(g.brand) + '&sbb_model=' + encodeURIComponent(g.model) + '&sbb_year=' + encodeURIComponent(g.year);
      // Ano com link próprio no menu: respeita o link; senão, coleção do modelo filtrada pela tag do ano.
      if (y && y.url && y.url !== model.url) g.url = y.url + (y.url.indexOf('?') > -1 ? '&' : '?') + q;
      else g.url = bikeUrl(g, q);
      return g;
    }

    /**
     * Grava a moto e mostra as peças. Hero com data-inline="true" e uma grade [data-inline-target] na página:
     * rola até a grade e espera a troca; só navega para a coleção se a grade falhar (ou passar de 10 s).
     */
    var commitSeq = 0, goText = go.textContent;
    function commit(g, source) {
      write(g, source);
      var target = go.dataset.inline === 'true' ? document.querySelector('[data-hauer-garage-products][data-inline-target]') : null;
      if (target && typeof target.hauerProducts === 'function' && target.offsetParent !== null) {
        var seq = ++commitSeq, settled = false;
        go.classList.add('is-loading'); go.setAttribute('aria-busy', 'true');
        if (go.dataset.finding) go.textContent = go.dataset.finding;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try { target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); } catch (e) { target.scrollIntoView(); }
        var done = function (r) {
          if (settled) return; settled = true;
          if (seq !== commitSeq) return; // outra moto escolhida depois desta: quem decide é o commit mais novo
          go.classList.remove('is-loading'); go.removeAttribute('aria-busy'); go.textContent = goText;
          if (r && r.ok) {
            var h = target.querySelector('[data-hauer-products-heading]');
            if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
            return;
          }
          if (r && r.stale) return;
          if (g.url) window.location.href = g.url; // a grade não carregou: a coleção da moto resolve
        };
        setTimeout(function () { done({ ok: false }); }, 10000);
        target.hauerProducts().then(done, function () { done({ ok: false }); });
        return;
      }
      if (go.dataset.navigate === 'true' && g.url) window.location.href = g.url;
    }

    go.addEventListener('click', function () {
      if (!current.brand || !current.model) return;
      if (selYear.value === '' && !yearOpt) return;
      var y = selYear.value === '' ? null : current.years[+selYear.value];
      commit(garageFor(current.brand, current.model, y), root.closest('[data-hauer-pdp]') ? 'pdp_selector' : 'hero');
    });

    /* ---------- Chips de 1 toque (12/10/2026, etapa A) ---------- */
    var quick = root.querySelector('[data-hauer-quick]');
    var genBox = quick && quick.querySelector('[data-hauer-quick-gen]');
    var genQ = quick && quick.querySelector('[data-hauer-quick-gen-q]');
    var genOpts = quick && quick.querySelector('[data-hauer-quick-gen-opts]');
    var genChoices = [];
    function chipModels(chip) {
      var urls = String(chip.dataset.urls || '').split(',').map(function (u) { return u.trim().split('?')[0]; }).filter(Boolean);
      var b = null;
      brands.forEach(function (x) { if (!b && x.title === chip.dataset.brand) b = x; });
      if (!b) return null;
      var ms = (b.models || []).filter(function (m) { return urls.indexOf(String(m.url || '').split('?')[0]) > -1; });
      return ms.length ? { brand: b, models: ms } : null;
    }
    function hideGen() {
      if (!genBox) return;
      genBox.hidden = true; genChoices = [];
      if (genOpts) genOpts.innerHTML = '';
      quick.querySelectorAll('[data-hauer-quick-bike]').forEach(function (c) { c.removeAttribute('aria-expanded'); });
    }
    function paintChips(g) {
      if (!quick) return;
      quick.querySelectorAll('[data-hauer-quick-bike]').forEach(function (c) {
        var on = !!(g && g.handle) && String(c.dataset.urls || '').split(',').some(function (u) { return u.trim().split('?')[0] === '/collections/' + g.handle; });
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    if (quick) quick.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : e.target.parentElement;
      var pick = t && t.closest('[data-hauer-quick-gen-pick]');
      if (pick) {
        var c = genChoices[+pick.dataset.hauerQuickGenPick];
        if (c) { hideGen(); commit(garageFor(c.brand, c.model, null), 'hero_chip'); }
        return;
      }
      var chip = t && t.closest('[data-hauer-quick-bike]');
      if (!chip) return;
      var r = chipModels(chip);
      if (!r) { hideGen(); selMake.focus(); return; } // menu mudou: segue pelos selects
      if (r.models.length === 1) { hideGen(); commit(garageFor(r.brand, r.models[0], null), 'hero_chip'); return; }
      genChoices = r.models.map(function (m) { return { brand: r.brand, model: m }; });
      if (genQ) genQ.textContent = fill(quick.dataset.which || 'Which {bike}?', { bike: chip.textContent.trim() });
      if (genOpts) genOpts.innerHTML = genChoices.map(function (c, i) {
        var rg = rangeOf(c.model.title);
        return '<button type="button" class="hauer-quick__chip hauer-quick__chip--gen" data-hauer-quick-gen-pick="' + i + '">' + esc(rg ? rg.text : c.model.title) + '</button>';
      }).join('');
      quick.querySelectorAll('[data-hauer-quick-bike]').forEach(function (c) { if (c === chip) c.setAttribute('aria-expanded', 'true'); else c.removeAttribute('aria-expanded'); });
      genBox.hidden = false;
      var first = genOpts && genOpts.querySelector('button'); if (first) first.focus();
    });

    // Moto salva: selects já preenchidos (como no protótipo)
    function prefill(g) {
      if (!g) return;
      var bi = -1; brands.forEach(function (b, i) { if (b.title === g.brand) bi = i; });
      if (bi < 0) return;
      silent = true;
      selMake.value = String(bi); selMake.dispatchEvent(new Event('change'));
      var mi = -1;
      (current.brand.models || []).forEach(function (m, i) {
        if (modelLabel(m) !== g.model) return;
        if (mi < 0 || (g.handle && (m.url || '').indexOf('/' + g.handle) > -1)) mi = i;
      });
      if (mi > -1) {
        selModel.value = String(mi); selModel.dispatchEvent(new Event('change'));
        var yi = -1; current.years.forEach(function (y, i) { if (String(y.value || y.title) === String(g.year)) yi = i; });
        if (yi > -1) { selYear.value = String(yi); go.disabled = false; }
      }
      silent = false;
    }

    function paint() {
      var g = read();
      if (card) card.classList.toggle('is-saved', !!g);
      if (savedEl) savedEl.textContent = g ? label(g) : '';
      paintChips(g);
    }
    if (changeBtn) changeBtn.addEventListener('click', function () { hideGen(); clear('hero_change'); selMake.focus(); });
    prefill(read());
    paint();
    onChange(function () { if (document.body.contains(root)) { prefill(read()); paint(); } });
  }

  /* ---------- Barra fixa "Your bike" / chip do header ---------- */
  function initBar(bar) {
    if (!once(bar)) return;
    var bikeEl = bar.querySelector('[data-hauer-bar-bike]');
    var withBike = bar.querySelectorAll('[data-hauer-bar-with]');
    var withoutBike = bar.querySelectorAll('[data-hauer-bar-without]');
    function paint() {
      var g = read();
      if (bikeEl) bikeEl.textContent = label(g);
      withBike.forEach(function (el) { el.hidden = !g; });
      withoutBike.forEach(function (el) { el.hidden = !!g; });
      bar.hidden = bar.dataset.hideWhenEmpty === 'true' && !g;
      bar.classList.toggle('has-bike', !!g); // 12/10/2026 (comentário 15a): estado verde com moto salva
    }
    bar.querySelectorAll('[data-hauer-bar-set], [data-hauer-bar-change]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        openGarage(bar.dataset.garageUrl || '/#hauer-garage');
      });
    });
    paint();
    onChange(function () { if (document.body.contains(bar)) paint(); });
  }

  /* ---------- Cards (snippets/hauer-card): etiqueta verde "Fits your bike" ---------- */
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  /** Família do tipo de peça: "F1 Frame Slider", "S1 Frame Slider" e "S Frame Slider" competem entre si. */
  function typeFamily(t) { return norm(String(t || '').replace(/^(F1|S1|S)\s+/i, '')); }
  /**
   * Encaixe de um card (data-fit-tags = tags do produto + "open" / "open:all") com a garagem.
   * 'yes' = marca, modelo e ano batem; 'check' = marca e modelo batem mas o produto não tem
   * tag de ano para conferir; 'no' = não encaixa. Faixa aberta: qualquer ano >= ano mínimo.
   */
  function fitState(tagsStr, g) {
    if (!g) return 'no';
    var tags = String(tagsStr || '').split('|');
    // peça universal (token "universal" do hauer-card, 24/09/2026): serve em qualquer moto
    if (tags.indexOf('universal') > -1) return 'yes';
    if (!bikeMatch(tags, g)) return 'no';
    var years = tags.filter(isYearTag);
    var open = tags.indexOf('open') > -1;
    var yearSet = /^\d{4}$/.test(String(g.year || ''));
    // 03/11/2026: moto gravada com uma geração no lugar do ano ("2021-up", menu de 3 níveis) vira faixa, nunca "serve tudo"
    if (!yearSet && g.year && g.year !== 'all' && !(typeof g.y2 === 'number' && g.y2 > 0)) { var gr = rangeOf('(' + g.year + ')'); if (gr) g = { brand: g.brand, model: g.model, year: 'all', y1: gr.y1, y2: gr.y2 }; }
    if (tags.indexOf('open:all') > -1) return 'yes';
    // 12/10/2026 (etapa A): "All years" de um modelo com faixa no menu (g.y1..g.y2) não aceita outra geração.
    var hasRange = !yearSet && typeof g.y2 === 'number' && g.y2 > 0;
    if (!yearSet && !hasRange) return 'yes';
    if (!years.length) return open ? 'yes' : 'check';
    if (hasRange) {
      var inRange = years.some(function (y) { return +y >= g.y1 && +y <= g.y2; });
      if (!inRange && open) inRange = Math.min.apply(null, years.map(Number)) <= g.y2;
      return inRange ? 'yes' : 'no';
    }
    if (years.indexOf(String(g.year)) > -1) return 'yes';
    if (open) { var min = Math.min.apply(null, years.map(Number)); if (+g.year >= min) return 'yes'; }
    return 'no';
  }
  /**
   * 12/10/2026 (etapa A): chaves de busca de um modelo do menu com apelidos separados por "|".
   * "GSX-S 1000 | 950" -> gsxs1000950, gsxs1000, gsxs950; "ZX-6R | RR" -> zx6r, zx6rr; "Z400 | Ninja 400" -> z400, ninja400;
   * "CB 500 F | R | X | Hornet" -> cb500f, cb500r, cb500x (palavra solta sem número, como "Hornet", não vira chave).
   */
  // 03/11/2026: apelidos da configuração "Model aliases" (data-aliases do JSON do menu): "YZF-R3 = R3" faz a moto
  // "YZF-R3" do menu reconhecer as peças que dizem "R3". O menu foi renomeado depois de 13/10 e perdeu peças.
  var ALIASES = null;
  function aliasMap() {
    if (ALIASES) return ALIASES;
    ALIASES = {};
    var el = document.querySelector('[data-hauer-garage-data][data-aliases]');
    String(el ? el.getAttribute('data-aliases') : '').split(/\r?\n/).forEach(function (line) {
      var i = line.indexOf('='); if (i < 1) return;
      var k = norm(line.slice(0, i)); if (!k) return;
      ALIASES[k] = (ALIASES[k] || []).concat(line.slice(i + 1).split('|').map(function (s) { return s.trim(); }).filter(Boolean));
    });
    return ALIASES;
  }
  function modelKeys(model) {
    var full = String(model || '');
    var alts = full.split(/\s*\|\s*/).filter(Boolean);
    var keys = [norm(full)];
    if (alts.length > 1) {
      var base = alts[0];
      keys.push(norm(base));
      alts.slice(1).forEach(function (a) {
        if (/^\d+$/.test(a)) keys.push(norm(base.replace(/\d+/, a)));
        else if (/^[a-z]{1,3}$/i.test(a)) { if (/[a-z]+\s*$/i.test(base)) keys.push(norm(base.replace(/[a-z]+\s*$/i, a))); }
        else if (/\d/.test(a) && /[a-z]/i.test(a)) keys.push(norm(a));
      });
    }
    var am = aliasMap(), extra = (am[norm(full)] || []).concat(alts.length > 1 ? am[norm(alts[0])] || [] : []);
    extra.forEach(function (a) { keys.push(norm(a)); });
    return keys.filter(function (k, i, arr) { return k && arr.indexOf(k) === i; });
  }
  /**
   * 03/11/2026 (Bruno: "seleciono minha moto e só aparece produto da minha moto"): valores para o filtro da Shopify por
   * tag (filter.p.tag, app Search & Discovery) a partir do modelo da moto salva: cada nome do menu ("MT-09 | FZ-09"),
   * os apelidos do tema e variações de espaço/hífen/maiúscula ("MT-09", "MT09", "MT 09", "FZ-09"...). Valores repetidos
   * do mesmo filtro são "ou" na Shopify: é o corte no servidor (todas as gerações do modelo); ano e geração ficam com
   * fitState no navegador. Medido no catálogo de 03/11: acha 84% das peças que servem; junto com a coleção da moto, 94%.
   */
  function tagValues(g) {
    if (!g || !g.model) return [];
    var base = String(g.model).replace(/\s*\(.*$/, '');
    var am = aliasMap();
    var out = [];
    base.split(/\s*[|\/]\s*/).filter(Boolean).concat(am[norm(base)] || []).forEach(function (p) {
      p = String(p).trim();
      [p, p.replace(/\s+/g, ''), p.replace(/-/g, ''), p.replace(/[\s-]+/g, ''), p.replace(/-/g, ' '), p.replace(/(\D)(\d)/, '$1 $2')].forEach(function (v) {
        v = v.trim();
        if (v.length < 2) return;
        [v, v.toUpperCase()].forEach(function (x) { if (out.indexOf(x) < 0) out.push(x); });
      });
    });
    return out;
  }
  function tagQuery(g) { return tagValues(g).map(function (v) { return 'filter.p.tag=' + encodeURIComponent(v); }).join('&'); }
  /** 03/11/2026: ano de verdade numa tag de 4 dígitos (1000, 1098, 1199, 1250 são modelos de moto, não anos). */
  function isYearTag(t) { return /^(19[5-9]\d|20\d\d)$/.test(String(t)); }
  /**
   * 02/11/2026: marca e modelo de uma peça contra a moto salva. A mesma regra no card, no carrinho e na página de
   * produto (hauer-product.js chama HauerGarage.bikeMatch). Antes a página de produto comparava o nome inteiro do menu
   * ("ZX-6R | RR") com as tags e dizia "não serve" em 333 das 1.193 peças das coleções de moto (catálogo de 02/11/2026).
   *  - marca: tag da marca ou o título da peça (token "ttl:<título>"; ex.: peça sem a tag "Yamaha" com "Yamaha" no título);
   *  - modelo: tag por qualquer apelido do menu (modelKeys) ou o título cita o modelo como palavra inteira
   *    ("ZX-6R" = "ZX6R"; "S1000 R" não vale para "S1000 RR"; "DRZ 400" não vale para "Z400"). No título, modelo seguido
   *    de letra solta, sufixo de 2 letras ou "Tracer" é outra moto ("CBR 1000 RR-R", "Z900 RS", "MT-09 Tracer"): não vale.
   *    "|", "/" e "," do título separam a lista de motos ("CB 500 F | X | Hornet" vale para a CB 500 F).
   * A coleção sozinha NÃO prova encaixe: há peças de outra moto dentro de coleções. Na dúvida, 'no' (nunca "serve" à toa).
   * Sem lookbehind no regex (Safari antigo dentro do Instagram não tem).
   */
  function bikeMatch(tags, g) {
    if (!g) return false;
    tags = (tags || []).map(String);
    var n = tags.map(norm), b = norm(g.brand), ttl = '';
    tags.forEach(function (t) { if (t.indexOf('ttl:') === 0) ttl = '-' + t.slice(4).toLowerCase().replace(/\s*[~|\/,]\s*/g, '~').replace(/[^a-z0-9~]+/g, '-') + '-'; });
    var keys = modelKeys(g.model);
    // 03/11/2026: tags do app de busca por moto ("2012-2024-ktm-duke-390-esr7049674") também reconhecem a moto, só por
    // marca + modelo exatos ("kawasaki-z900" não vale para Z900 RS). Os anos dessas tags não entram: o app tem anos errados.
    var esr = tags.some(function (t) { var m = /^\d{4}-\d{4}-([a-z0-9-]+)-esr\d+$/i.exec(t); if (!m) return false; var s = norm(m[1]); return keys.some(function (k) { return s === b + k; }); });
    if (esr) return true;
    if (!b || (n.indexOf(b) < 0 && norm(ttl).indexOf(b) < 0)) return false;
    if (keys.some(function (k) { return n.indexOf(k) > -1; })) return true;
    return !!ttl && keys.some(function (k) {
      return new RegExp('[^a-z0-9]' + k.split('').join('[^a-z0-9~]*') + '(?=[^a-z0-9])(?!-(?:[a-z]{1,2}|tracer)[^a-z0-9])').test(ttl);
    });
  }
  function fits(tagsStr, g) { return fitState(tagsStr, g) !== 'no'; }
  function paintCards(root) {
    var g = read();
    (root || document).querySelectorAll('[data-hauer-card]').forEach(function (card) {
      var tag = card.querySelector('[data-hauer-card-tag]');
      if (!tag) return;
      if (tag.dataset.orig === undefined) { tag.dataset.orig = tag.hidden ? '' : tag.textContent; tag.dataset.origClass = tag.className; }
      var state = fitState(card.dataset.fitTags, g);
      if (state !== 'no' && !/--sale/.test(tag.dataset.origClass)) {
        tag.hidden = false;
        if (state === 'check') { tag.textContent = card.dataset.fitCheckLabel || 'Check years'; tag.className = 'hauer-card__tag hauer-card__tag--check'; }
        else { tag.textContent = card.dataset.fitLabel || 'Fits your bike'; tag.className = 'hauer-card__tag hauer-card__tag--fit'; }
      } else {
        tag.className = tag.dataset.origClass; tag.textContent = tag.dataset.orig; tag.hidden = !tag.dataset.orig;
      }
    });
    paintCartLines(root, g);
  }
  /* A-11 (3): "Fits your {bike}" / "Made for {fits}. Your bike is a {bike}." em cada linha do carrinho. */
  function paintCartLines(root, g) {
    (root || document).querySelectorAll('[data-hauer-cart-line]').forEach(function (line) {
      var out = line.querySelector('[data-hauer-cart-fitcheck]');
      if (!out) return;
      if (!g || !line.dataset.fitTags) { out.hidden = true; out.textContent = ''; out.className = 'hauer-cart-fitcheck'; return; }
      var state = fitState(line.dataset.fitTags, g);
      var vars = { bike: label(g), fits: line.dataset.fitLine || '', year: g.year };
      out.hidden = false;
      out.textContent = fill(state === 'yes' ? out.dataset.yes : state === 'check' ? out.dataset.check : out.dataset.no, vars);
      out.className = 'hauer-cart-fitcheck hauer-cart-fitcheck--' + state;
    });
  }
  window.HauerGarage.fits = fits;
  window.HauerGarage.fitState = fitState;
  window.HauerGarage.bikeMatch = bikeMatch;
  window.HauerGarage.modelKeys = modelKeys;
  window.HauerGarage.paintCards = paintCards;
  window.HauerGarage.tagValues = tagValues;
  window.HauerGarage.tagQuery = tagQuery;

  /* ---------- Fragmento da coleção da moto (Section Rendering API, A-05) ---------- */
  var fragCache = {};
  /** Uma requisição por URL de fragmento (home, página de produto e drawer partilham a resposta). */
  function getFragment(url) {
    if (!fragCache[url]) {
      fragCache[url] = fetch(url).then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); });
      fragCache[url].catch(function () { delete fragCache[url]; });
    }
    return fragCache[url];
  }
  /** Busca a grade da coleção da moto (com o ano, quando houver); vazia com o ano, tenta sem ele. */
  function fetchGrid(g, withYear, sectionFile) {
    var q = 'section_id=' + (sectionFile || SECTION_FILE) + '&sort_by=best-selling';
    var url = withYear ? bikeUrl(g, q) : bikeUrl({ handle: g.handle, year: 'all' }, q);
    return getFragment(url).then(function (html) {
      var fresh = parseGrid(html);
      if (fresh) return fresh;
      if (withYear && /^\d{4}$/.test(String(g.year || ''))) return fetchGrid(g, false, sectionFile);
      return null;
    });
  }
  function parseGrid(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-hauer-products]');
    return fresh && fresh.children.length ? fresh : null;
  }
  /**
   * Escolhe os <li> do fragmento: descarta a própria peça e o mesmo tipo (página de produto),
   * descarta handles/tipos já no carrinho (drawer), prefere um card por tipo, corta em `limit`.
   */
  function pickCards(fresh, opts) {
    var items = Array.prototype.slice.call(fresh.children);
    var skipH = (opts.skipHandles || []).map(String), skipT = (opts.skipTypes || []).map(typeFamily);
    var ok = items.filter(function (li) {
      var c = li.querySelector('[data-hauer-card]'); if (!c) return false;
      if (skipH.indexOf(c.dataset.handle) > -1) return false;
      if (c.dataset.type && skipT.indexOf(typeFamily(c.dataset.type)) > -1) return false;
      if (opts.fitOnly && opts.garage && fitState(c.dataset.fitTags, opts.garage) === 'no') return false;
      return true;
    });
    // Drawer: peças baratas que completam o kit primeiro (axle sliders, spools, bar ends), com
    // preferência por 1 variante (botão "Add" direto): peça com opções paga uma penalidade igual ao
    // preço da mais barata. Assim um bar end de $69 com "Pick options" vence um escapamento de $765.
    if (opts.preferSingle) {
      var price = function (li) { var t = (li.querySelector('.hauer-card__pr') || {}).textContent || ''; var m = /([\d,]+(?:\.\d+)?)/.exec(t.replace(/\s/g, '')); return m ? parseFloat(m[1].replace(/,/g, '')) : 1e9; };
      var multi = function (li) { return +li.querySelector('[data-hauer-card]').dataset.variants > 1 ? 1 : 0; };
      var minPrice = ok.length ? Math.min.apply(null, ok.map(price)) : 0;
      var score = function (li) { return price(li) + (multi(li) ? minPrice : 0); };
      ok.sort(function (a, b) { return (score(a) - score(b)) || (multi(a) - multi(b)); });
    }
    // Com moto salva, as peças feitas para a moto vêm antes das universais (12/10/2026): na Z900,
    // 7 dos 12 primeiros cards eram universais e as peças da própria moto ficavam no fim.
    if (opts.garage) {
      var isUni = function (li) { var c = li.querySelector('[data-hauer-card]'); return String(c.dataset.fitTags || '').split('|').indexOf('universal') > -1; };
      ok = ok.filter(function (li) { return !isUni(li); }).concat(ok.filter(isUni));
    }
    var seen = [], first = [], rest = [];
    ok.forEach(function (li) {
      var t = typeFamily(li.querySelector('[data-hauer-card]').dataset.type || '');
      if (!t || seen.indexOf(t) < 0) { seen.push(t); first.push(li); } else rest.push(li);
    });
    return first.concat(rest).slice(0, opts.limit || 8);
  }

  /* ---------- Best sellers para a moto (troca a grade via Section Rendering API) ---------- */
  function initProducts(sec) {
    if (!once(sec)) return;
    var grid = sec.querySelector('[data-hauer-products]');
    var heading = sec.querySelector('[data-hauer-products-heading]');
    var sub = sec.querySelector('[data-hauer-products-sub]');
    var viewAll = sec.querySelector('[data-hauer-products-viewall]');
    var setBtn = sec.querySelector('[data-hauer-products-set]');
    var sectionFile = /^[a-z0-9-]+$/.test(sec.dataset.sectionId || '') && sec.dataset.sectionId.indexOf('template--') !== 0 ? sec.dataset.sectionId : SECTION_FILE;
    if (!grid) return;
    var baseHtml = grid.innerHTML;
    var baseHeading = heading ? heading.textContent : '';
    var baseSub = sub ? sub.textContent : '';
    var baseSubHidden = sub ? sub.hidden : false;
    var baseHref = viewAll ? viewAll.getAttribute('href') : '';
    var tplHeading = sec.dataset.headingForBike || 'Parts for your {bike}';
    var tplSub = sec.dataset.subForBike || '';
    var subError = sec.dataset.subError || '';
    var badge = sec.dataset.fitsLabel || 'Fits your bike';
    var limit = parseInt(sec.dataset.limit, 10) || grid.children.length || 8;
    var curHandle = sec.dataset.currentHandle || '', curType = sec.dataset.currentType || '';
    var serverBike = sec.dataset.bikeHandle || '';
    // 12/10/2026 (etapa A): grade da home que o hero troca (data-inline-target): título com contagem,
    // eyebrow e link trocam com a moto; sec.hauerProducts() devolve a promessa do último paint ({ ok, n }).
    var inline = sec.hasAttribute('data-inline-target');
    var eyebrow = sec.querySelector('[data-hauer-products-eyebrow]');
    var baseEyebrow = eyebrow ? eyebrow.textContent : '';
    var baseViewLabel = viewAll ? viewAll.textContent : '';
    var baseViewOpen = viewAll ? viewAll.hasAttribute('data-hauer-garage-open') : false;
    var lastResult = Promise.resolve({ ok: false });
    sec.hauerProducts = function () { return lastResult; };
    var forced = sec.dataset.forceHandle ? { handle: sec.dataset.forceHandle, year: 'all', brand: sec.dataset.forceBrand || '', model: sec.dataset.forceModel || '', forced: true } : null;
    var lastKey = null;

    // Página de produto sem moto salva: acha a moto da peça pelas tags (marca, modelo, anos)
    var fallback = null;
    if (sec.dataset.fallbackTags) {
      try {
        var rawTags = JSON.parse(sec.dataset.fallbackTags);
        var tagsN = rawTags.map(norm);
        var prodYears = rawTags.filter(function (t) { return /^\d{4}$/.test(t); });
        var dataEl = sec.querySelector('[data-hauer-garage-data]') || document.querySelector('[data-hauer-garage-data]');
        var brandsData = dataEl ? JSON.parse(dataEl.textContent) : [];
        brandsData.forEach(function (b) {
          if (fallback || tagsN.indexOf(norm(b.title)) < 0) return;
          (b.models || []).forEach(function (m) {
            if (fallback) return;
            var ml = modelLabel(m);
            if (tagsN.indexOf(norm(ml)) < 0) return;
            var yrs = yearsFor(m).map(function (y) { return String(y.value); }).filter(function (v) { return /^\d{4}$/.test(v); });
            var overlap = !yrs.length || !prodYears.length || yrs.some(function (y) { return prodYears.indexOf(y) > -1; });
            if (!overlap) return;
            fallback = { brand: b.title, model: ml, year: 'all', handle: ((m.url || '').split('?')[0].match(/\/collections\/([^/?#]+)/) || [])[1] || '', url: m.url, fallback: true };
          });
        });
      } catch (e) { fallback = null; }
    }

    function addBadges() {
      grid.querySelectorAll('.card--product').forEach(function (c) {
        if (c.querySelector('.hauer-fits-badge')) return;
        var b = document.createElement('span'); b.className = 'hauer-fits-badge'; b.textContent = badge; c.appendChild(b);
      });
    }
    function restore(errorText) {
      grid.innerHTML = baseHtml;
      if (heading) heading.textContent = baseHeading;
      if (sub) { sub.textContent = errorText && !serverBike ? errorText : baseSub; sub.hidden = errorText && !serverBike ? false : baseSubHidden; }
      if (viewAll) {
        viewAll.setAttribute('href', baseHref);
        if (inline) { viewAll.textContent = baseViewLabel; if (baseViewOpen) viewAll.setAttribute('data-hauer-garage-open', ''); }
      }
      if (eyebrow) eyebrow.textContent = baseEyebrow;
      paintCards(grid); paintShipDates(grid);
    }
    function paint() {
      var g = forced || read() || fallback;
      if (setBtn) setBtn.hidden = !!read();
      if (!g || !g.handle) {
        if (lastKey !== null) restore();
        lastKey = null; lastResult = Promise.resolve({ ok: false }); return;
      }
      // Página de produto: a grade já veio do servidor com as peças da mesma moto (A-05).
      if (serverBike && g.handle === serverBike) {
        if (lastKey !== null) restore();
        lastKey = 'server:' + serverBike; lastResult = Promise.resolve({ ok: true }); return;
      }
      // A faixa entra na chave: "All years" de outra geração com a mesma coleção repinta.
      var key = g.handle + '/' + (g.year || 'all') + (g.range ? '/' + g.range : '');
      if (key === lastKey) return;
      lastKey = key;
      sec.classList.add('is-loading');
      lastResult = fetchGrid(g, true, sectionFile)
        .then(function (fresh) {
          if (key !== lastKey) return { ok: false, stale: true }; // outra moto foi escolhida no meio da busca
          // Contagem para "{n} parts fit your {bike}" (antes do pickCards mover os <li>): products_count do
          // fragmento menos as peças buscadas que fitState descartou.
          var countN = 0;
          if (fresh && inline) {
            var total = parseInt(fresh.getAttribute('data-count'), 10);
            var fetchedN = fresh.children.length;
            var fitN = Array.prototype.filter.call(fresh.children, function (li) { var c = li.querySelector('[data-hauer-card]'); return c && fitState(c.dataset.fitTags, g) !== 'no'; }).length;
            countN = total > 0 ? Math.max(fitN, total - (fetchedN - fitN)) : fitN;
          }
          // 24/09/2026 (QA V-05): com moto salva, cards que não servem (fitState 'no') saem antes do corte;
          // 'check' (sem tags de ano) fica. Remover: tirar fitOnly/garage e o bikeCopy abaixo.
          var picked = fresh ? pickCards(fresh, { skipHandles: curHandle ? [curHandle] : [], skipTypes: curType ? [curType] : [], fitOnly: !g.fallback && !g.forced, garage: g, limit: limit }) : [];
          // Poucas peças que servem: mantém o título/sub padrão, nunca 'Every item below fits' sobre peças soltas.
          var bikeCopy = picked.length >= (curHandle ? 2 : 3);
          if (picked.length < (curHandle ? 2 : 1)) {
            if (forced) { sec.hidden = true; return { ok: false }; }
            restore(subError); return { ok: false };
          }
          grid.innerHTML = '';
          picked.forEach(function (li) { grid.appendChild(li); });
          if (!g.fallback && !g.forced) addBadges();
          paintCards(grid);
          paintShipDates(grid);
          if (inline && !g.fallback && !forced) {
            // Home: título sempre com a moto e a contagem real (as peças já passaram pelo fitState).
            var n = Math.max(countN, picked.length);
            var tplN = n === 1 ? (sec.dataset.headingOne || tplHeading) : (sec.dataset.headingCount || tplHeading);
            if (heading) heading.textContent = fill(tplN, { n: n, bike: label(g) });
            if (sub) { sub.textContent = fill(tplSub, { bike: label(g) }); sub.hidden = !sub.textContent; }
            if (eyebrow && sec.dataset.eyebrowBike) eyebrow.textContent = sec.dataset.eyebrowBike;
            if (viewAll) {
              viewAll.setAttribute('href', g.url || bikeUrl(g));
              viewAll.removeAttribute('data-hauer-garage-open');
              if (viewAll.dataset.labelBike) viewAll.textContent = viewAll.dataset.labelBike;
            }
          } else if (!forced) {
            if (!bikeCopy) {
              if (heading) heading.textContent = baseHeading;
              if (sub) { sub.textContent = baseSub; sub.hidden = baseSubHidden; }
            } else {
              if (heading) heading.textContent = (g.fallback ? (sec.dataset.headingFallback || tplHeading) : tplHeading).replace('{bike}', label(g));
              if (sub) { sub.textContent = g.fallback ? baseSub : tplSub.replace('{bike}', label(g)); sub.hidden = g.fallback ? baseSubHidden : !sub.textContent; }
            }
            if (viewAll) viewAll.setAttribute('href', g.url || bikeUrl(g));
          }
          if (window.theme && typeof window.theme.initLazyImages === 'function') window.theme.initLazyImages();
          return { ok: true, n: picked.length };
        })
        .catch(function (err) {
          if (window.console && console.warn) console.warn('[hauer-garage] products fragment failed', err);
          if (key !== lastKey) return { ok: false, stale: true };
          if (forced) { sec.hidden = true; return { ok: false }; }
          restore(subError);
          return { ok: false };
        })
        .then(function (res) { if (key === lastKey) sec.classList.remove('is-loading'); return res; });
    }
    if (setBtn) setBtn.addEventListener('click', function () { openGarage(setBtn.dataset.garageUrl || '/#hauer-garage'); });
    paint();
    onChange(function () { if (document.body.contains(sec)) paint(); });
  }

  /* ---------- Categorias da home: contagem da moto salva (12/10/2026, etapa B) ----------
   * Remover: apagar este bloco e a linha de initCats em initIn (os tiles voltam à contagem global). */
  function priceOf(card) {
    var el = card.querySelector('.hauer-card__pr'); if (!el) return null;
    var cl = el.cloneNode(true); var was = cl.querySelector('s'); if (was) was.remove();
    var m = /([^\d\s]{0,3}\s?\d[\d.,]*)/.exec((cl.textContent || '').replace(/\s+/g, ' '));
    if (!m) return null;
    var v = parseFloat(m[1].replace(/[^\d.]/g, ''));
    return isFinite(v) ? { v: v, text: m[1].trim() } : null;
  }
  function initCats(sec) {
    if (!once(sec)) return;
    var tiles = Array.prototype.slice.call(sec.querySelectorAll('[data-hauer-cat]'));
    var tpl = sec.dataset.countBike || '{n} for your {bike}';
    var fromWord = sec.dataset.from || 'from';
    var lastKey = null;
    // text: null = contagem do catálogo (sem moto); '' = nenhuma contagem; texto = contagem da moto
    function setTile(t, text) {
      var glob = t.querySelector('[data-hauer-cat-count]'), mine = t.querySelector('[data-hauer-cat-bike]');
      if (glob) glob.hidden = text !== null;
      if (mine) { mine.textContent = text || ''; mine.hidden = !text; }
    }
    function paint() {
      var g = read();
      if (!g || !g.handle) { lastKey = null; tiles.forEach(function (t) { setTile(t, null); }); return; }
      var key = g.handle + '/' + (g.year || 'all') + (g.range ? '/' + g.range : '');
      if (key === lastKey) return;
      lastKey = key;
      tiles.forEach(function (t) { setTile(t, ''); });
      var short = String(g.model || '').replace(/\s*\|\s*/g, ' / ');
      fetchGrid(g, true).then(function (fresh) {
        if (key !== lastKey || !fresh) return;
        var cards = Array.prototype.filter.call(fresh.querySelectorAll('[data-hauer-card]'), function (c) { return fitState(c.dataset.fitTags, g) !== 'no'; });
        // Coleção maior que o fragmento (data-count > cards buscados): a conta vira "{n}+" e o "from $X" sai
        // (a peça mais barata pode estar fora do fragmento). Hoje a maior coleção de moto do menu tem 46 (< 50).
        var total = parseInt(fresh.getAttribute('data-count'), 10);
        var partial = total > fresh.children.length;
        tiles.forEach(function (t) {
          var words = String(t.dataset.types || '').split(',').map(norm).filter(Boolean);
          if (!words.length) return;
          var hit = cards.filter(function (c) { var ty = norm(c.dataset.type); return words.some(function (w) { return ty.indexOf(w) > -1; }); });
          if (!hit.length) return;
          var min = null;
          if (!partial) hit.forEach(function (c) { var p = priceOf(c); if (p && (!min || p.v < min.v)) min = p; });
          setTile(t, fill(tpl, { n: hit.length + (partial ? '+' : ''), bike: short }) + (min ? ' · ' + fromWord + ' ' + min.text : ''));
        });
      }).catch(function () { /* sem a grade da moto: fica sem contagem, nunca a do catálogo */ });
    }
    paint();
    onChange(function () { if (document.body.contains(sec)) paint(); });
  }

  /* ---------- Bundle do mês só para a moto dele (12/10/2026, comentário 7 do Anderson) ----------
   * data-bundle-fit-tags = campo "fit_tags" da seção hauer-monthly-bundle: "BMW, S1000 RR, 2023-2026"
   * (marca, modelo, anos; "2023+" = daquele ano em diante). Campo próprio porque as tags dos produtos
   * enganam (o bar end do bundle leva anos do G310R). Moto salva que não casa (fitState 'no'): a seção
   * some. Sem moto salva, campo vazio ou editor de temas: mostra. Remover: apagar este bloco e a linha
   * de initBundleFit em initIn. */
  function bundleTags(str) {
    var out = [];
    String(str || '').split(/[|,]/).forEach(function (raw) {
      var t = raw.trim(); if (!t) return;
      var m = /^(\d{4})\s*(?:\+|[-–]\s*(?:on|up|newer|present))$/i.exec(t);
      if (m) { out.push(m[1], 'open'); return; }
      m = /^(\d{4})\s*[-–]\s*(\d{4})$/.exec(t);
      if (m && +m[2] >= +m[1] && +m[2] - +m[1] < 60) { for (var y = +m[1]; y <= +m[2]; y++) out.push(String(y)); return; }
      out.push(t);
    });
    return out.join('|');
  }
  /* 12/10/2026 (etapa B): "All years" de uma geração ("S1000 RR (2019-on)", y1/y2) só vê o bundle se os anos do
   * bundle cobrem a geração inteira. Antes bastava um ano em comum: um bundle 2023-2026 aparecia para quem tem
   * uma 2019-2022. Com ano escolhido (g.year = 2024), quem decide é o fitState, como antes. */
  function bundleCoversRange(tagsStr, g) {
    if (!g || !g.y1 || (g.year && g.year !== 'all')) return true;
    var parts = String(tagsStr || '').split('|');
    var ys = parts.filter(function (t) { return /^\d{4}$/.test(t); }).map(Number);
    if (!ys.length) return true;
    var lo = Math.min.apply(null, ys), hi = parts.indexOf('open') > -1 ? 9999 : Math.max.apply(null, ys);
    return lo <= g.y1 && hi >= Math.min(g.y2 || 9999, new Date().getFullYear() + 1);
  }
  function initBundleFit(el) {
    // guarda própria: data-hauer-ready do mesmo elemento é do initBundle (hauer-home.js)
    if (!el || el.dataset.hauerFitReady) return;
    el.dataset.hauerFitReady = '1';
    var tags = bundleTags(el.dataset.bundleFitTags);
    var box = el.closest('.shopify-section') || el;
    // 12/10/2026 (etapa B): data-bundle-need-bike = sem moto salva também esconde (CSS já esconde até aqui).
    var needBike = el.hasAttribute('data-bundle-need-bike');
    function paint() {
      var g = read();
      var design = !!(window.Shopify && window.Shopify.designMode);
      var hide = !design && ((needBike && !(g && g.handle)) || !!(g && tags && (fitState(tags, g) === 'no' || !bundleCoversRange(tags, g))));
      box.style.display = hide ? 'none' : '';
      el.classList.toggle('is-shown', !hide);
      if (hide) box.setAttribute('data-hauer-bundle-hidden', ''); else box.removeAttribute('data-hauer-bundle-hidden');
    }
    paint();
    onChange(function () { if (document.body.contains(el)) paint(); });
  }

  /* ---------- Drawer: "Complete your {bike} setup" (A-11) ---------- */
  var upsellTimer = null, upsellAdded = [];
  function cartHandles() {
    var hs = [], ts = [];
    document.querySelectorAll('[data-hauer-cart-line]').forEach(function (l) { if (l.dataset.productHandle) hs.push(l.dataset.productHandle); if (l.dataset.productType) ts.push(l.dataset.productType); });
    return { handles: hs.concat(upsellAdded), types: ts };
  }
  function paintUpsell(force) {
    var box = document.querySelector('[data-hauer-cart-upsell]');
    if (!box) return;
    // Só busca o fragmento com a gaveta aberta (ou abrindo): nada de requisição extra em cada página.
    var drawer = box.closest('cart-drawer');
    if (!force && drawer && drawer.getAttribute('open') === null && drawer.getAttribute('aria-hidden') !== 'false') return;
    var g = read();
    var list = box.querySelector('[data-hauer-cart-upsell-list]');
    var head = box.querySelector('[data-hauer-cart-upsell-heading]');
    if (!g || !g.handle || !list) { box.hidden = true; return; }
    var inCart = cartHandles();
    // 03/11/2026: carrinho vazio não mostra sugestão (ficava embaixo de "Your cart is empty")
    if (!document.querySelector('[data-hauer-cart-line]')) { box.hidden = true; box.dataset.key = ''; return; }
    var key = g.handle + '/' + (g.year || 'all') + '|' + inCart.handles.join(',');
    if (box.dataset.key === key) return;
    box.dataset.key = key;
    fetchGrid(g, true)
      .then(function (fresh) {
        if (!fresh) { box.hidden = true; return; }
        var picked = pickCards(fresh, { skipHandles: inCart.handles, skipTypes: inCart.types, preferSingle: true, fitOnly: true, garage: g, limit: parseInt(box.dataset.limit, 10) || 2 });
        if (!picked.length) { box.hidden = true; return; }
        if (head) head.textContent = fill(box.dataset.heading || 'Complete your {bike} setup', { bike: label(g) });
        list.innerHTML = picked.map(function (li) {
          var c = li.querySelector('[data-hauer-card]');
          var img = c.querySelector('.hauer-card__ph img');
          var name = (c.querySelector('.hauer-card__nm') || {}).textContent || '';
          var ft = (c.querySelector('.hauer-card__ft') || {}).textContent || '';
          var prEl = c.querySelector('.hauer-card__pr'), pr = '';
          if (prEl) { var prClone = prEl.cloneNode(true); var was = prClone.querySelector('s'); if (was) was.remove(); pr = prClone.textContent || ''; }
          var single = !(+c.dataset.variants > 1) && c.dataset.variantId;
          var note = box.dataset.bundleNote ? ' <small>' + esc(box.dataset.bundleNote) + '</small>' : '';
          var action = single
            ? '<button type="button" class="hauer-btn hauer-btn--dark hauer-cup__add" data-hauer-cup-add="' + esc(c.dataset.variantId) + '" data-handle="' + esc(c.dataset.handle) + '">' + esc(box.dataset.add || 'Add') + '</button>'
            : '<a class="hauer-btn hauer-btn--ghost hauer-cup__add" href="' + esc(c.getAttribute('href')) + '">' + esc(box.dataset.pick || 'Pick options') + '</a>';
          return '<li class="hauer-cup__row">' +
            '<a class="hauer-cup__img" href="' + esc(c.getAttribute('href')) + '" tabindex="-1" aria-hidden="true">' + (img ? '<img src="' + esc(img.currentSrc || img.src) + '" alt="" width="44" height="44" loading="lazy">' : '') + '</a>' +
            '<div class="hauer-cup__bd"><a class="hauer-cup__nm" href="' + esc(c.getAttribute('href')) + '">' + esc(name.trim()) + '</a>' +
            // 03/11/2026: peça sem ano cadastrado mostra "Check years" (como o card e a linha do carrinho), não "✓ Fits"
            (fitState(c.dataset.fitTags, g) === 'check'
              ? '<span class="hauer-cup__ft">' + esc(c.dataset.fitCheckLabel || 'Check years') + (ft ? ' · ' + esc(ft.trim()) : '') + '</span>'
              : '<span class="hauer-cup__ft">✓ ' + esc(box.dataset.fits || 'Fits your bike') + (ft ? ' · ' + esc(ft.trim()) : '') + '</span>') +
            '<span class="hauer-cup__pr">' + esc(pr.replace(/\s+/g, ' ').trim()) + note + '</span></div>' +
            action + '</li>';
        }).join('');
        box.hidden = false;
      })
      .catch(function (err) { if (window.console && console.warn) console.warn('[hauer-garage] cart upsell failed', err); box.hidden = true; });
  }
  function scheduleUpsell(force) { clearTimeout(upsellTimer); upsellTimer = setTimeout(function () { try { paintUpsell(force); } catch (e) { /* noop */ } }, 120); }
  function initCartUpsell() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-hauer-cup-add]');
      if (!btn || btn.disabled) return;
      var box = btn.closest('[data-hauer-cart-upsell]');
      btn.disabled = true; btn.classList.add('is-loading');
      var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      root = String(root).replace(/\/$/, '');
      fetch(root + '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: +btn.dataset.hauerCupAdd, quantity: 1 }) })
        .then(function (r) { return r.json().then(function (d) { if (!r.ok || (d && d.status >= 400)) throw new Error(d && d.description || r.status); return d; }); })
        .then(function () {
          upsellAdded.push(btn.dataset.handle);
          btn.textContent = box && box.dataset.added || 'Added';
          track('cart_upsell_add', { handle: btn.dataset.handle, variant: btn.dataset.hauerCupAdd });
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          setTimeout(function () { if (box) box.dataset.key = ''; paintUpsell(); }, 900);
        })
        .catch(function () {
          btn.disabled = false; btn.classList.remove('is-loading');
          var err = box && box.querySelector('[data-hauer-cart-upsell-error]');
          if (err) { err.hidden = false; setTimeout(function () { err.hidden = true; }, 4000); }
        });
    });
    ['on:cart-drawer:before-open', 'on:cart-drawer:after-open', 'on:cart:add', 'on:line-item:change', 'dispatch:cart-drawer:refresh'].forEach(function (ev) {
      var force = ev === 'on:cart-drawer:before-open' || ev === 'on:cart-drawer:after-open';
      document.addEventListener(ev, function () { scheduleUpsell(force); setTimeout(function () { scheduleUpsell(force); }, 1200); });
    });
    onChange(function () { upsellAdded = []; scheduleUpsell(); });
  }

  /* ---------- Ganchos pequenos (A-13 newsletter, A-19 links da moto salva) ---------- */
  function paintHooks(root) {
    var g = read();
    (root || document).querySelectorAll('[data-hauer-news-tags]').forEach(function (inp) {
      var base = inp.dataset.hauerNewsTags || inp.dataset.baseTags || 'newsletter';
      inp.dataset.baseTags = base;
      inp.value = g ? base + ',bike:' + label(g) : base;
    });
    // Carrinho vazio: "Shop parts for my bike" leva à coleção da moto salva.
    (root || document).querySelectorAll('.cart-empty a[href]').forEach(function (a) {
      if (!a.dataset.hauerHref) a.dataset.hauerHref = a.getAttribute('href');
      a.setAttribute('href', g && g.handle ? (g.url ? g.url.split('?')[0] : bikeUrl(g)) : a.dataset.hauerHref);
    });
    // Learn: "Watch the install videos" procura o canal pelo modelo salvo.
    (root || document).querySelectorAll('.hauer-station__tool a[href*="youtube.com/@"], .hauer-rail__tool a[href*="youtube.com/@"]').forEach(function (a) {
      if (!a.dataset.hauerHref) a.dataset.hauerHref = a.getAttribute('href');
      var base = a.dataset.hauerHref.split('/search')[0].replace(/\/$/, '');
      a.setAttribute('href', g ? base + '/search?query=' + encodeURIComponent([g.brand, g.model].join(' ')) : a.dataset.hauerHref);
    });
  }

  /* ---------- Boot (A-22): escopo por raiz, guardas, listeners únicos ---------- */
  function initIn(root) {
    root = root || document;
    paintCards(root);
    paintShipDates(root);
    paintHooks(root);
    root.querySelectorAll('[data-hauer-garage-selector]').forEach(initSelector);
    root.querySelectorAll('[data-hauer-garage-bar]').forEach(initBar);
    root.querySelectorAll('[data-hauer-garage-products]').forEach(initProducts);
    root.querySelectorAll('[data-bundle-fit-tags], [data-bundle-need-bike]').forEach(initBundleFit);
    root.querySelectorAll('[data-hauer-cats]').forEach(initCats);
  }
  function init() {
    if (!window.HauerGarage.__cardsBound) {
      window.HauerGarage.__cardsBound = true;
      onChange(function () { paintCards(document); paintHooks(document); });
      // Drawer do Enterprise re-renderiza o carrinho: reaplica datas de envio, encaixe por linha e links.
      ['on:cart:add', 'on:line-item:change', 'on:cart-drawer:before-open', 'on:cart-drawer:after-open', 'on:cart:change', 'dispatch:cart-drawer:refresh'].forEach(function (ev) {
        document.addEventListener(ev, function () { setTimeout(function () { paintShipDates(document); paintCartLines(document, read()); paintHooks(document); }, 50); setTimeout(function () { paintCartLines(document, read()); }, 1000); });
      });
      document.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-hauer-garage-open]');
        if (b) { e.preventDefault(); openGarage(b.dataset.garageUrl || b.getAttribute('href') || '/#hauer-garage'); }
      });
      initCartUpsell();
    }
    if (window.location.hash === '#hauer-garage') {
      var card = document.querySelector('[data-hauer-garage-card]');
      if (card) { card.scrollIntoView({ block: 'center' }); var sel = card.querySelector('select'); if (sel) setTimeout(function () { sel.focus(); }, 300); }
    }
    initIn(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', function (e) { initIn(e && e.target ? e.target : document); });
})();
